import React from 'react';
import { format, parseISO } from 'date-fns';
import { Battery, CheckCircle2, AlertTriangle, HelpCircle, Pencil, UserPen } from 'lucide-react';
import { TNaerasPoolState } from '@/data/types';
import { POOL_SOURCE_META } from '../utils/pool';

interface PoolStatusCardProps {
  pool: TNaerasPoolState | null;
  isLoading: boolean;
  /** Opens the manual edit; the button is prominent while the pool is stale. */
  onEdit: () => void;
}

/**
 * The number every bid is capped by, and how much it can be trusted.
 *
 * `reconciled` and `manual` are healthy; `stale` means Job A hasn't settled yet
 * — the state after an ACROSS outage, which the manual edit is there to fix —
 * and `seed` means there is no history at all, so a 0 MWh seed must not read
 * like a real current figure.
 */
const TONES = {
  manual: {
    icon: UserPen,
    card: 'border-indigo-200 bg-gradient-to-br from-indigo-50/80 via-white to-violet-50/40',
    chip: 'bg-indigo-100 text-indigo-800',
    iconBox: 'bg-gradient-to-br from-indigo-500 to-violet-600',
    value: 'text-indigo-950',
  },
  reconciled: {
    icon: CheckCircle2,
    card: 'border-emerald-200 bg-gradient-to-br from-emerald-50/80 via-white to-teal-50/40',
    chip: 'bg-emerald-100 text-emerald-800',
    iconBox: 'bg-gradient-to-br from-emerald-500 to-teal-600',
    value: 'text-emerald-950',
  },
  stale: {
    icon: AlertTriangle,
    card: 'border-amber-200 bg-gradient-to-br from-amber-50/80 via-white to-orange-50/40',
    chip: 'bg-amber-100 text-amber-900',
    iconBox: 'bg-gradient-to-br from-amber-500 to-orange-600',
    value: 'text-amber-950',
  },
  seed: {
    icon: HelpCircle,
    card: 'border-slate-200 bg-gradient-to-br from-slate-50/80 via-white to-gray-50/40',
    chip: 'bg-slate-200 text-slate-800',
    iconBox: 'bg-gradient-to-br from-slate-500 to-slate-700',
    value: 'text-slate-900',
  },
} as const;

const mwh = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : `${value.toFixed(2)} MWh`;

export const PoolStatusCard: React.FC<PoolStatusCardProps> = ({ pool, isLoading, onEdit }) => {
  if (isLoading && !pool) {
    return (
      <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-xl shadow-gray-900/5">
        <div className="h-20 animate-pulse rounded-2xl bg-gray-100" />
      </div>
    );
  }

  if (!pool) {
    return (
      <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-xl shadow-gray-900/5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-200 text-gray-600">
              <Battery className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Απόθεμα Υβριδικής Ενέργειας</h3>
              <p className="text-xs text-gray-500">
                Μη διαθέσιμο — δεν υπάρχει ιστορικό ούτε αρχική τιμή.
              </p>
            </div>
          </div>
          <button
            onClick={onEdit}
            className="flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-emerald-700"
          >
            <Pencil className="h-3.5 w-3.5" />
            Ορισμός τιμής
          </button>
        </div>
      </div>
    );
  }

  const tone = TONES[pool.source];
  const Icon = tone.icon;
  const needsAttention = pool.source === 'stale' || pool.source === 'seed';

  return (
    <div className={`rounded-3xl border ${tone.card} p-6 shadow-xl shadow-gray-900/5`}>
      <div className="flex flex-wrap items-center justify-between gap-5">
        <div className="flex items-center gap-4">
          <div
            className={`flex h-14 w-14 items-center justify-center rounded-2xl ${tone.iconBox} text-white shadow-lg`}
          >
            <Battery className="h-7 w-7" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-bold text-gray-900">Απόθεμα Υβριδικής Ενέργειας</h3>
              <span
                className={`flex items-center gap-1 rounded-full px-3 py-0.5 text-[11px] font-bold ${tone.chip}`}
              >
                <Icon className="h-3 w-3" />
                {POOL_SOURCE_META[pool.source].label}
              </span>
            </div>
            <p className="mt-0.5 text-xs text-gray-600">
              Ισχύει στις 00:00 της {format(parseISO(pool.as_of), 'dd/MM/yyyy')}
              {pool.source === 'stale' && ` — ${pool.stale_days} ημέρες πίσω`}
              {pool.source === 'seed' && ' — δεν έχει γίνει ποτέ εκκαθάριση'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-baseline gap-1.5">
            <span className={`text-4xl font-black ${tone.value}`}>{pool.value.toFixed(2)}</span>
            <span className="text-sm font-semibold text-gray-500">MWh</span>
          </div>
          <button
            onClick={onEdit}
            className={
              needsAttention
                ? 'flex items-center gap-1.5 rounded-xl bg-amber-600 px-3 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-amber-700'
                : 'flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50'
            }
          >
            <Pencil className="h-3.5 w-3.5" />
            {needsAttention ? 'Ορισμός τιμής' : 'Επεξεργασία'}
          </button>
        </div>
      </div>

      {pool.source === 'manual' && pool.manual && (
        <p className="mt-4 rounded-2xl bg-white/70 p-3 text-xs text-gray-700">
          Ορίστηκε χειροκίνητα στις {format(new Date(pool.manual.set_at), 'dd/MM/yyyy HH:mm')} και
          αντικατέστησε τα {mwh(pool.manual.previous_value)}.{' '}
          {pool.manual.reconciled_value === null
            ? 'Η Εργασία Α δεν έχει υπολογίσει ακόμη την ημέρα.'
            : `Η Εργασία Α υπολόγισε ${mwh(pool.manual.reconciled_value)}.`}
        </p>
      )}

      {needsAttention && (
        <p className="mt-4 rounded-2xl bg-white/70 p-3 text-xs text-gray-700">
          {pool.source === 'stale'
            ? 'Η Εργασία Α δεν έχει ολοκληρωθεί επιτυχώς, οπότε χρησιμοποιείται η τελευταία γνωστή τιμή. Οι νέες προσφορές θα υπολογιστούν πάνω σε αυτήν — αν η πραγματική τιμή είναι γνωστή, ορίστε την χειροκίνητα.'
            : 'Δεν υπάρχει ιστορικό εκκαθάρισης, οπότε χρησιμοποιείται η ρυθμισμένη αρχική τιμή. Αν είναι 0, οι υβριδικές μονάδες δεν θα έχουν περιθώριο μεταβολής.'}
        </p>
      )}
    </div>
  );
};

export default PoolStatusCard;
